import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, AlertTriangle, Clock, Package } from 'lucide-react';
import Card from '../common/Card';
import StatusIndicator, { getStockLevel, getStockLevelColor } from '../common/StatusIndicator';
import { useApi } from '../../hooks/useApi';
import { getStockHealth, getZoneAnalytics } from '../../api/client';
import { formatDistanceToNow } from 'date-fns';

const ZONES = [
  'RASMUSTAV', 'Clear_Sky', 'SIDOROVICH', 'Bradislav',
  'Wirpmenistan', 'Briggosia', 'GREENMOUNTAIN',
];

export default function ZoneHealth() {
  const navigate = useNavigate();
  const { data: health, loading: healthLoading } = useApi(getStockHealth);
  const { data: analytics, loading: analyticsLoading } = useApi(getZoneAnalytics);

  const loading = healthLoading || analyticsLoading;

  const zoneData = React.useMemo(() => {
    const healthMap = {};
    if (health) {
      const zones = Array.isArray(health) ? health : health.zones || [];
      zones.forEach((z) => {
        healthMap[z.zone || z.name] = z;
      });
    }

    const analyticsMap = {};
    if (analytics) {
      const zones = Array.isArray(analytics) ? analytics : analytics.zones || [];
      zones.forEach((z) => {
        analyticsMap[z.zone || z.name] = z;
      });
    }

    return ZONES.map((zone) => {
      const h = healthMap[zone] || {};
      const a = analyticsMap[zone] || {};
      return {
        name: zone,
        stockPercent: h.stockPercent ?? h.healthPercent ?? h.stock_percent ?? 50,
        lowStockItems: h.lowStockItems ?? h.low_stock_items ?? 0,
        totalItems: h.totalItems ?? h.total_items ?? 0,
        mostTraded: a.mostTraded ?? a.most_traded ?? '—',
        lastTransaction: a.lastTransaction ?? a.last_transaction ?? null,
      };
    });
  }, [health, analytics]);

  return (
    <Card title="Zone Health" icon={MapPin} className="h-full">
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="skeleton h-24 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[360px] overflow-y-auto">
          {zoneData.map((zone) => {
            const level = getStockLevel(zone.stockPercent);
            const color = getStockLevelColor(zone.stockPercent);

            return (
              <div
                key={zone.name}
                onClick={() => navigate(`/zones/${encodeURIComponent(zone.name)}`)}
                className="bg-terminal-bg border border-terminal-border rounded-lg p-3 cursor-pointer hover:border-text-muted transition-all duration-200 group"
                style={{ borderLeftColor: color, borderLeftWidth: '3px' }}
              >
                {/* Zone Name + Status */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono font-medium text-text-primary group-hover:text-accent-green transition-colors">
                    {zone.name}
                  </span>
                  <StatusIndicator type="stock" value={zone.stockPercent} />
                </div>

                {/* Progress Bar */}
                <div className="w-full h-1.5 bg-terminal-border rounded-full mb-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, zone.stockPercent)}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-[10px] text-text-muted">
                  <div className="flex items-center gap-1">
                    {zone.lowStockItems > 0 && (
                      <span className="flex items-center gap-0.5 text-accent-amber">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        {zone.lowStockItems} low
                      </span>
                    )}
                    <span className="flex items-center gap-0.5">
                      <Package className="w-2.5 h-2.5" />
                      {zone.totalItems}
                    </span>
                  </div>
                  {zone.lastTransaction && (
                    <span className="flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {formatDistanceToNow(new Date(zone.lastTransaction), {
                        addSuffix: true,
                      })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
